import * as React from 'react';

import { InlineField, KEEP_EDITING, ReasonDialog, type InlineAnswer } from '@/design';
import type { IEtagged } from '@/domains/api';
import {
  ResponseField,
  decodeAnswer,
  domAddress,
  encodeAnswer,
  formatDateTime,
  gridRows,
  isCellsField,
  isMalformed,
  malformedHint,
  rowWriteColumns,
  sameAnswer,
  writableCells,
  type AnswerPayload,
  type IAnswerWrite,
  type IFinding,
  type IRenderState,
  type ISnapshotUsage,
  type UiValue
} from '@/domains/survey';
import { NO_ANSWER, answerText } from '../answer-text';
import { oversightApi } from '../api/oversight-api';
import {
  CORRECTION_REASON_MAX,
  type IOversightAnswer,
  type IOversightResponseDetail,
  type ITimelineRow
} from '../api/oversight-contracts';
import { conflictSentence, correctionRefusal } from '../correction-refusal';
import { refusalMessage } from '../refusal';
import { timelineActorText } from './timeline-values';

/** What the Answers tab hands every correction on it — one object, made once per tab. */
export interface ICorrections {
  responseId: string;
  /** The detail's `ETag`, quoted back verbatim as `If-Match` — never null here: a tab without one
   *  draws no correction (R5). */
  etag: string;
  participantName: string;
  /** Takes a correction's 200 — the whole detail and its `ETag` — as the page's read. */
  adopt: (next: IEtagged<IOversightResponseDetail>) => void;
  /** Reads the detail again; resolves `false` when the read failed. */
  reread: () => Promise<boolean>;
  /** The last reason typed per address, kept by the tab so a refused correction is saved again
   *  without retyping it (R6) — optional since SR-403, kept all the same when one was typed. */
  reasons: Map<string, string>;
  /** A sentence said beside an answer that may no longer draw its correction once the page is
   *  read again (a status that moved) — the tab draws it there; `null` forgets it. */
  tell: (address: string, sentence: Nullable<string>) => void;
  /** The id of the tab's `NotPermitted` sentence where the viewer lacks `responses.annotate`
   *  (SR-228): every pencil is drawn off, described by it; absent, they are on. */
  notPermitted?: string;
}

/**
 * One stored answer corrected in place — survey-api's correction, one address at a time (SR-222's
 * F4: W3, R1, R2, R6, R7).
 *
 * **The control is the respondent's own** (R1): `ResponseField`, the only place a `fieldType`
 * becomes a control, drawn over the response's definition snapshot and a synthesized render
 * document — every option offered, no variant, labels as authored, nothing required, no finding,
 * no prefill strip — exactly as the version preview draws the section view. A second mapping in
 * this domain would agree with the form until the day it did not. A grid's or a year table's
 * answer is one row, so the table is drawn with that row alone. It sits in a `form`, so its
 * `data-address` reads as it does on the respondent's form. Offering an option the engine would
 * hide is not a second evaluator: survey-api's matrix and its finding rule judge the write.
 *
 * **One correction is one write, one address** (R2): the payload is the runtime's `encodeAnswer`,
 * a value no payload can carry is held with the runtime's hint and sends nothing, a value saved
 * unchanged sends nothing, and an emptied control is a clear (`value: null`). Save confirms in a
 * `ReasonDialog` whose reason is **optional** (SR-403, the owner's ruling: the figures' dialog —
 * focus in the box, Enter confirms; a blank one is sent as none, survey-api's 2,000 said) — Cancel
 * there returns the field to editing with nothing said (`KEEP_EDITING`). The 200's body and its
 * `ETag` are adopted as the page's read: the answer is the read, so nothing is read again.
 *
 * **Each answer in words** is `correction-refusal.ts`' (R7): a refusal about the reason is said in
 * the dialog, which stays open; anything else closes it and is said beside the answer with the
 * draft kept. A 409 `revision-conflict` reads the response again before saying anything, adopting
 * the re-read so the next Save quotes its `ETag`, and says whether *this* answer changed — and to
 * what, by whom — or another one; nobody's value is overwritten unseen.
 *
 * **Nothing is sent over a value the person never saw.** Another field's write re-reads the page,
 * which can move this answer — and the `ETag` — under an open draft; a Save then would quote a
 * revision that already holds somebody else's value and land over it. So the value the draft
 * started from is noted as the pencil opens it (`onStart`), and a Save or a clear over a stored
 * value that has moved since is said as R7's conflict is, nothing sent; the next Save sends over
 * the value now shown. A date half typed is held, not cleared: the date input hands over `''` for
 * it as for a date emptied on purpose, and only the element knows which (`AdminInlineField`'s rule).
 *
 * The field settles only once the dialog has gone (`onClosed`), so `InlineField`'s focus rule
 * meets a page with nothing focused rather than a dialog on its way out: focus goes back to the
 * pencil when the correction landed, into the control otherwise.
 */
export function AnswerCorrection({
  label,
  snapshotUsage,
  instanceId,
  instanceKey,
  stored,
  display,
  corrections
}: {
  /** The question as the tab draws it, with the row's address where the usage holds several. */
  label: string;
  snapshotUsage: ISnapshotUsage;
  instanceId: Nullable<string>;
  instanceKey: string;
  /** The stored answer at this address, or `null` where nothing is stored — a question nobody
   *  answered, which a correction answers (the plan's Q1, ruled (a)). */
  stored: Nullable<IOversightAnswer>;
  /** The value as read. */
  display: React.ReactNode;
  corrections: ICorrections;
}) {
  const { responseId, etag, participantName, adopt, reread, reasons, tell, notPermitted } =
    corrections;
  const { fieldType } = snapshotUsage;
  // A table row's writable cells (SR-416): a correction of a row sends them and only them, and
  // survey-api refuses a computed, N/A or heading cell `cell_read_only` for staff too.
  const columns = rowWriteColumns(snapshotUsage, instanceKey);
  const address = domAddress(snapshotUsage.code, instanceId, instanceKey);
  const storedValue = stored?.value ?? null;
  const value = React.useMemo(() => decodeAnswer(storedValue, fieldType), [storedValue, fieldType]);

  // What the open draft started from — the stored value as the pencil opened it.
  const base = React.useRef<Nullable<AnswerPayload>>(storedValue);
  const form = React.useRef<HTMLFormElement>(null);
  const halfTyped = () =>
    [...(form.current?.querySelectorAll('input') ?? [])].some((input) => input.validity.badInput);

  const [dialog, setDialog] = React.useState<Nullable<{ write: IAnswerWrite }>>(null);
  const [open, setOpen] = React.useState(false);
  // The field's save waits on the dialog: it is settled with `outcome` once the dialog has gone.
  const settle = React.useRef<Nullable<(answer: InlineAnswer) => void>>(null);
  const outcome = React.useRef<InlineAnswer>(KEEP_EDITING);

  const ask = (payload: Nullable<AnswerPayload>): Promise<InlineAnswer> =>
    new Promise((resolve) => {
      settle.current = resolve;
      outcome.current = KEEP_EDITING;
      // What an earlier correction here was told is spent once another is asked for.
      tell(address, null);
      setDialog({ write: { usage: snapshotUsage.code, value: payload, instanceId, instanceKey } });
      setOpen(true);
    });

  /** R7's sentence when the stored value moved under the open draft since the pencil opened it —
   *  the draft then rebased on the value now shown — or `null` when it did not. */
  const movedSinceStart = async (): Promise<Nullable<string>> => {
    if (sameAnswer(base.current, storedValue)) return null;
    base.current = storedValue;
    return conflictSentence(nowText(stored ?? undefined), await lastChange());
  };

  const onSave = async (draft: UiValue): Promise<InlineAnswer> => {
    if (halfTyped() || isMalformed(draft, fieldType, columns)) return malformedHint(fieldType);
    const moved = await movedSinceStart();
    if (moved) return moved;
    const payload = encodeAnswer(draft, fieldType, columns);
    if (sameAnswer(payload, writableCells(storedValue, columns))) return null;
    return ask(payload);
  };

  const onClear = async (): Promise<InlineAnswer> => (await movedSinceStart()) ?? ask(null);

  /** A 409 `revision-conflict`: read again, adopt the read, and say what changed (R7). The
   *  sentence is told to the tab before the read is adopted: a read without this answer — cleared
   *  meanwhile — would otherwise take the question, and the correction open on it, off the page. */
  const conflict = async (): Promise<string> => {
    let fresh: IEtagged<IOversightResponseDetail>;
    try {
      fresh = await oversightApi.getResponse(responseId);
    } catch (error) {
      return (
        'The response changed since you opened it, and could not be read again ' +
        `(${refusalMessage(error, 'the read failed')}) — your correction was not sent. ` +
        'Reload the page before saving again.'
      );
    }
    const now = findAnswer(fresh.body, snapshotUsage.code, instanceId, instanceKey);
    const nowValue = now?.value ?? null;
    const sentence = sameAnswer(nowValue, base.current)
      ? conflictSentence(null, null)
      : conflictSentence(nowText(now), await lastChange());
    base.current = nowValue;
    tell(address, sentence);
    adopt(fresh);
    return sentence;
  };

  /** Who changed this answer last, and when — the newest row of the question's history at this
   *  address — or `null` when it cannot be read. Names an account as the timeline does. */
  const lastChange = async (): Promise<Nullable<string>> => {
    try {
      const page = await oversightApi.timeline(responseId, { usage: snapshotUsage.code });
      const row = page.items.find(
        (item) => item.instanceId === instanceId && item.instanceKey === instanceKey
      );
      return row ? changedBy(row) : null;
    } catch {
      return null;
    }
  };

  const confirm = async (reason: string): Promise<Nullable<string>> => {
    if (!dialog) return null;
    reasons.set(address, reason);
    try {
      // The dialog hands the reason over trimmed; a blank one is none (SR-403).
      adopt(
        await oversightApi.correct(responseId, etag, {
          reason: reason === '' ? null : reason,
          writes: [dialog.write]
        })
      );
      reasons.delete(address);
      tell(address, null);
      outcome.current = null;
      return null;
    } catch (error) {
      const refusal = correctionRefusal(error);
      if (refusal.kind === 'reason') return refusal.sentence;
      if (refusal.kind === 'conflict') {
        outcome.current = await conflict();
      } else {
        outcome.current = refusal.sentence;
        if (refusal.reread) {
          // Said where the tab can still draw it if the re-read takes this correction away.
          tell(address, refusal.sentence);
          if (!(await reread())) {
            outcome.current =
              `${refusal.sentence} The page could not be read again — reload it before saving ` +
              'again.';
            tell(address, outcome.current);
          }
        }
      }
      // Closes the dialog: what was said belongs beside the answer, with the draft kept.
      return null;
    }
  };

  const onClosed = () => {
    const resolve = settle.current;
    settle.current = null;
    setDialog(null);
    resolve?.(outcome.current);
  };

  const clearing = dialog?.write.value === null;
  const cellsRow = isCellsField(fieldType)
    ? { key: instanceKey, label: rowLabel(snapshotUsage, instanceKey) }
    : null;

  return (
    <>
      <InlineField<UiValue>
        label={label}
        name={participantName}
        fieldKey={address}
        display={display}
        value={value}
        multiline={fieldType === 'text_area'}
        isEmpty={(draft) =>
          !halfTyped() &&
          !isMalformed(draft, fieldType, columns) &&
          encodeAnswer(draft, fieldType, columns) === null
        }
        notPermitted={notPermitted}
        onStart={() => {
          base.current = storedValue;
        }}
        onSave={onSave}
        onClear={onClear}
        renderControl={({ value: draft, onChange, disabled }) => (
          <form ref={form} onSubmit={(event) => event.preventDefault()}>
            {cellsRow ? (
              <ResponseField
                usage={snapshotUsage}
                instanceId={instanceId}
                render={SYNTHESIZED_RENDER}
                value=""
                findings={NO_FINDINGS}
                required={false}
                readOnly={disabled}
                onChange={() => undefined}
                cellRows={[cellsRow]}
                cellValues={new Map([[cellsRow.key, draft as Record<string, string>]])}
                onCellChange={(_, cells) => onChange(cells)}
              />
            ) : (
              <ResponseField
                usage={snapshotUsage}
                instanceId={instanceId}
                render={SYNTHESIZED_RENDER}
                value={draft}
                findings={NO_FINDINGS}
                required={false}
                readOnly={disabled}
                onChange={onChange}
              />
            )}
          </form>
        )}
      />
      {/* Mounted for one correction at a time, not once per answer on the tab. */}
      {dialog && (
        <ReasonDialog
          open={open}
          onOpenChange={setOpen}
          onClosed={onClosed}
          title={clearing ? 'Clear this answer' : 'Correct this answer'}
          description={
            `${label} for ${participantName}. A reason, where you give one, is kept with the ` +
            'change on the response’s timeline.'
          }
          required={false}
          enterConfirms
          max={CORRECTION_REASON_MAX}
          initialReason={reasons.get(address) ?? ''}
          confirmLabel={clearing ? 'Clear it' : 'Correct it'}
          onConfirm={confirm}
        />
      )}
    </>
  );
}

/**
 * The engine's document, synthesized as the version preview synthesizes it: an absent entry is
 * read as "no override" — every option offered, no variant, labels as authored — and nothing is
 * marked required, since requiredness is the engine's verdict and the engine does not run here.
 */
const SYNTHESIZED_RENDER: IRenderState = {
  usageVisibility: {},
  sectionVisibility: {},
  required: {},
  activeVariants: {},
  optionVisibility: {},
  textOverrides: {}
};

/** No finding is drawn on a correction: the oversight surface names none. */
const NO_FINDINGS: IFinding[] = [];

/** A grid row's heading is its option's label; a year table's row is headed by its key, since the
 *  cycle's year labels are not in the snapshot. */
function rowLabel(usage: ISnapshotUsage, key: string): string {
  return gridRows(usage).find((row) => row.key === key)?.label ?? key;
}

function findAnswer(
  detail: IOversightResponseDetail,
  usage: string,
  instanceId: Nullable<string>,
  instanceKey: string
): IOversightAnswer | undefined {
  for (const section of detail.sections) {
    const found = section.usages.find((candidate) => candidate.code === usage);
    if (found) {
      return found.answers.find(
        (answer) => answer.instanceId === instanceId && answer.instanceKey === instanceKey
      );
    }
  }
  return undefined;
}

/** An answer's value after a change, as the conflict sentence says it. */
function nowText(answer: IOversightAnswer | undefined): string {
  return answer ? answerText(answer) : `unanswered (${NO_ANSWER})`;
}

function changedBy(row: ITimelineRow): string {
  const who = timelineActorText(row) ?? 'an unknown account';
  return row.occurredAt
    ? `changed by ${who}, ${formatDateTime(row.occurredAt)}`
    : `changed by ${who}`;
}
